import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  Dropdown,
  Option,
  Combobox,
  Spinner,
  MessageBar,
  MessageBarBody,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { ComplianceObligation } from '../../types';
import type { CreateObligationPayload } from '../../services/complianceApi';
import { useCreateObligation, useUpdateObligation } from '../../services/complianceApi';
import { CalendarDateInput } from '../dates/CalendarDateInput';
import { cadenceName } from '../../utils/cadenceName';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
  },
  errorBar: {
    marginBottom: tokens.spacingVerticalS,
  },
});

const STATES = ['FL', 'GA'];
const FREQUENCIES = ['Monthly', 'Quarterly', 'SemiAnnual', 'Annual'] as const;

// Suggested values for the source-records context. Both fields accept free text
// (Combobox), so staff can type anything; the suggestions just speed the common case.
const INFORMATION_SOURCE_SUGGESTIONS = [
  'Bank Statements',
  'POS Statements',
  'QuickBooks Online',
  'Xero (In House)',
  'Submit Invoices',
  'Other',
];
const ACCESS_OF_INFORMATION_SUGGESTIONS = [
  'Direct access (we have credentials)',
  'Client emails monthly',
  'Client uploads via portal',
  'Pickup in person',
  'Postal mail',
  'Other',
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** Pass an existing obligation to switch to edit mode. */
  obligation?: ComplianceObligation;
  /**
   * STX-AUDIT-01: called when an edit SAVED but its "Form changed" history entry wasn't recorded, so the page can say so and offer a
   * retry (Christian, 2026-11-02: never silent).
   */
  onHistoryNotRecorded?: (info: { obligationId: string; clientId: string; before: string | null; after: string | null }) => void;
}

interface FormState {
  state: string;
  obligationType: string;
  obligationDisplayName: string;
  frequency: string;
  dueDayOffset: string;
  leadTimeDays: string;
  formNumber: string;
  accountNumber: string;
  startDate: string;
  informationSource: string;
  accessOfInformation: string;
  clientHandlesPayment: boolean;
}

const EMPTY_FORM: FormState = {
  state: '',
  obligationType: '',
  obligationDisplayName: '',
  frequency: '',
  dueDayOffset: '20',
  leadTimeDays: '10',
  formNumber: '',
  accountNumber: '',
  startDate: '',
  informationSource: '',
  accessOfInformation: '',
  clientHandlesPayment: false,
};

function formFromObligation(o: ComplianceObligation): FormState {
  return {
    state: o.state,
    obligationType: o.obligationType,
    obligationDisplayName: o.obligationDisplayName,
    frequency: o.frequency,
    dueDayOffset: String(o.dueDayOffset),
    leadTimeDays: String(o.leadTimeDays),
    formNumber: o.formNumber ?? '',
    accountNumber: o.accountNumber ?? '',
    startDate: o.startDate ?? '',
    informationSource: o.informationSource ?? '',
    accessOfInformation: o.accessOfInformation ?? '',
    clientHandlesPayment: o.clientHandlesPayment ?? false,
  };
}

export default function AddObligationDialog({ open, onOpenChange, clientId, obligation, onHistoryNotRecorded }: Props) {
  const styles = useStyles();
  const { t } = useTranslation();
  const isEdit = !!obligation;

  const [form, setForm] = useState<FormState>(isEdit ? formFromObligation(obligation) : EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createMutation = useCreateObligation();
  const updateMutation = useUpdateObligation();

  // Sync form when obligation prop changes (e.g. switching between edit targets)
  useEffect(() => {
    if (open) {
      setForm(obligation ? formFromObligation(obligation) : EMPTY_FORM);
      setErrors({});
      setSubmitError(null);
    }
  }, [open, obligation]);

  function setField<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.state) next.state = t('addObligationDialog.validation.stateRequired');
    if (!form.obligationType.trim()) next.obligationType = t('addObligationDialog.validation.obligationTypeRequired');
    if (!form.obligationDisplayName.trim()) next.obligationDisplayName = t('addObligationDialog.validation.displayNameRequired');
    if (!form.frequency) next.frequency = t('addObligationDialog.validation.frequencyRequired');
    const offset = parseInt(form.dueDayOffset, 10);
    if (isNaN(offset) || offset < 1 || offset > 31) {
      next.dueDayOffset = t('addObligationDialog.validation.dueDayOffsetRange');
    }
    const lead = parseInt(form.leadTimeDays, 10);
    if (isNaN(lead) || lead < 0) {
      next.leadTimeDays = t('addObligationDialog.validation.leadTimeMin');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSubmitError(null);

    const payload: CreateObligationPayload = {
      state: form.state,
      obligationType: form.obligationType.trim(),
      obligationDisplayName: form.obligationDisplayName.trim(),
      frequency: form.frequency,
      dueDayOffset: parseInt(form.dueDayOffset, 10),
      leadTimeDays: parseInt(form.leadTimeDays, 10),
      formNumber: form.formNumber.trim() || undefined,
      accountNumber: form.accountNumber.trim() || undefined,
      startDate: form.startDate || undefined,
      informationSource: form.informationSource.trim() || undefined,
      accessOfInformation: form.accessOfInformation.trim() || undefined,
      clientHandlesPayment: form.clientHandlesPayment,
    };

    try {
      if (isEdit && obligation) {
        const saved = await updateMutation.mutateAsync({ id: obligation.id, payload });
        if (saved.historyNotRecorded) {
          onHistoryNotRecorded?.({
            obligationId: obligation.id,
            clientId,
            before: saved.historyNotRecorded.before,
            after: saved.historyNotRecorded.after,
          });
        }
      } else {
        await createMutation.mutateAsync({ clientId, payload });
      }
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('addObligationDialog.unexpectedError'));
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(_e, data) => { if (!isPending) onOpenChange(data.open); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isEdit ? t('addObligationDialog.titleEdit') : t('addObligationDialog.titleAdd')}</DialogTitle>
          <DialogContent>
            {submitError && (
              <MessageBar intent="error" className={styles.errorBar}>
                <MessageBarBody>{submitError}</MessageBarBody>
              </MessageBar>
            )}
            <div className={styles.form}>
              <div className={styles.row}>
                <Field label={t('addObligationDialog.state')} required validationMessage={errors.state}>
                  <Dropdown
                    placeholder={t('addObligationDialog.selectState')}
                    value={form.state}
                    selectedOptions={form.state ? [form.state] : []}
                    onOptionSelect={(_e, data) => setField('state', data.optionValue ?? '')}
                  >
                    {STATES.map((s) => (
                      <Option key={s} value={s}>{s}</Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label={t('addObligationDialog.frequency')} required validationMessage={errors.frequency}>
                  <Dropdown
                    placeholder={t('addObligationDialog.selectFrequency')}
                    value={form.frequency ? cadenceName(t, form.frequency) : ''}
                    selectedOptions={form.frequency ? [form.frequency] : []}
                    onOptionSelect={(_e, data) => setField('frequency', data.optionValue ?? '')}
                  >
                    {FREQUENCIES.map((f) => (
                      <Option key={f} value={f}>{cadenceName(t, f)}</Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>

              <Field label={t('addObligationDialog.obligationType')} required validationMessage={errors.obligationType}>
                <Input
                  value={form.obligationType}
                  onChange={(_e, data) => setField('obligationType', data.value)}
                  placeholder={t('addObligationDialog.obligationTypePlaceholder')}
                />
              </Field>

              <Field label={t('addObligationDialog.displayName')} required validationMessage={errors.obligationDisplayName}>
                <Input
                  value={form.obligationDisplayName}
                  onChange={(_e, data) => setField('obligationDisplayName', data.value)}
                  placeholder={t('addObligationDialog.displayNamePlaceholder')}
                />
              </Field>

              <div className={styles.row}>
                <Field
                  label={t('addObligationDialog.dueDayOffset')}
                  required
                  hint={t('addObligationDialog.dueDayOffsetHint')}
                  validationMessage={errors.dueDayOffset}
                >
                  <Input
                    type="number"
                    value={form.dueDayOffset}
                    onChange={(_e, data) => setField('dueDayOffset', data.value)}
                  />
                </Field>
                <Field
                  label={t('addObligationDialog.leadTime')}
                  required
                  hint={t('addObligationDialog.leadTimeHint')}
                  validationMessage={errors.leadTimeDays}
                >
                  <Input
                    type="number"
                    value={form.leadTimeDays}
                    onChange={(_e, data) => setField('leadTimeDays', data.value)}
                  />
                </Field>
              </div>

              <div className={styles.row}>
                <Field label={t('addObligationDialog.formNumber')} validationMessage={errors.formNumber}>
                  <Input
                    value={form.formNumber}
                    onChange={(_e, data) => setField('formNumber', data.value)}
                    placeholder={t('addObligationDialog.formNumberPlaceholder')}
                  />
                </Field>
                <Field label={t('addObligationDialog.accountNumber')} validationMessage={errors.accountNumber}>
                  <Input
                    value={form.accountNumber}
                    onChange={(_e, data) => setField('accountNumber', data.value)}
                    placeholder={t('addObligationDialog.accountNumberPlaceholder')}
                  />
                </Field>
              </div>

              <div className={styles.row}>
                <Field
                  label={t('addObligationDialog.informationSource')}
                  hint={t('addObligationDialog.informationSourceHint')}
                >
                  <Combobox
                    placeholder={t('addObligationDialog.informationSourcePlaceholder')}
                    freeform
                    value={form.informationSource}
                    selectedOptions={form.informationSource ? [form.informationSource] : []}
                    onOptionSelect={(_e, data) => setField('informationSource', data.optionValue ?? '')}
                    onChange={(e) => setField('informationSource', (e.target as HTMLInputElement).value)}
                  >
                    {INFORMATION_SOURCE_SUGGESTIONS.map((s) => (
                      <Option key={s} value={s}>{s}</Option>
                    ))}
                  </Combobox>
                </Field>
                <Field
                  label={t('addObligationDialog.access')}
                  hint={t('addObligationDialog.accessHint')}
                >
                  <Combobox
                    placeholder={t('addObligationDialog.accessPlaceholder')}
                    freeform
                    value={form.accessOfInformation}
                    selectedOptions={form.accessOfInformation ? [form.accessOfInformation] : []}
                    onOptionSelect={(_e, data) => setField('accessOfInformation', data.optionValue ?? '')}
                    onChange={(e) => setField('accessOfInformation', (e.target as HTMLInputElement).value)}
                  >
                    {ACCESS_OF_INFORMATION_SUGGESTIONS.map((s) => (
                      <Option key={s} value={s}>{s}</Option>
                    ))}
                  </Combobox>
                </Field>
              </div>

              <Field
                label={t('addObligationDialog.paymentHandling')}
                hint={t('addObligationDialog.paymentHandlingHint')}
              >
                <Switch
                  checked={form.clientHandlesPayment}
                  onChange={(_e, data) => setForm((prev) => ({ ...prev, clientHandlesPayment: data.checked }))}
                  label={form.clientHandlesPayment ? t('addObligationDialog.clientHandlesPayment') : t('addObligationDialog.ptcSchedulesPayment')}
                />
              </Field>

              <Field label={t('addObligationDialog.startDate')} hint={t('addObligationDialog.startDateHint')}>
                <CalendarDateInput value={form.startDate} onChange={(v) => setField('startDate', v)} />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={isPending} onClick={() => onOpenChange(false)}>
              {t('addObligationDialog.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabled={isPending}
              icon={isPending ? <Spinner size="tiny" /> : undefined}
              onClick={() => { void handleSubmit(); }}
            >
              {isEdit ? t('addObligationDialog.saveChanges') : t('addObligationDialog.titleAdd')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
